import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string;
  /** Non-blocking guidance ("Letters, numbers and underscores"). Hidden while an error shows. */
  hint?: string;
  /** Extra element id appended to `aria-describedby` — e.g. a password checklist. */
  describedBy?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  containerClassName?: string;
};

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

/**
 * Text input with optional label, leading/trailing icons and error message.
 * The ref is forwarded to the inner <input> element.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    error,
    hint,
    describedBy,
    leftIcon,
    rightIcon,
    containerClassName,
    className,
    id,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  // The hint is replaced by the error, never stacked under it — two competing
  // descriptions of the same field read as a contradiction. `describedBy` is
  // additive so a password input can point at its error AND its live checklist.
  const describedByIds =
    [error && errorId, !error && hint && hintId, describedBy]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className={cn('w-full', containerClassName)}>
      {label && (
        <label
          htmlFor={inputId}
          className="mb-1.5 block text-sm font-semibold text-ink-secondary"
        >
          {label}
        </label>
      )}
      <div
        className={cn(
          'flex items-center gap-2 rounded-2xl border bg-surface px-3 transition-all duration-200',
          error ? 'border-error' : 'border-line focus-within:border-primary',
        )}
      >
        {leftIcon && <span className="shrink-0 text-ink-tertiary">{leftIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedByIds}
          className={cn(
            'min-w-0 flex-1 bg-transparent py-3 text-base text-ink outline-none placeholder:text-ink-tertiary',
            // The wrapper shows focus via focus-within border; suppress the
            // global focus-visible ring on the inner input itself.
            'focus-visible:ring-0 focus-visible:ring-offset-0',
            className,
          )}
          {...rest}
        />
        {rightIcon && <span className="shrink-0 text-ink-tertiary">{rightIcon}</span>}
      </div>
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-error-strong">
          {error}
        </p>
      ) : (
        hint && (
          <p id={hintId} className="mt-1 text-xs text-ink-tertiary">
            {hint}
          </p>
        )
      )}
    </div>
  );
});
